import { useState } from 'react';
import {
  MEETING_TEMPLATE_COLORS,
  type MeetingTemplateColor,
  type MeetingTemplateKind,
  type MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { Button } from '../button/button.tsx';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog.tsx';
import { Input } from '../input/input.tsx';
import { Select } from '../select/select.tsx';
import { SettingsRow, SettingsValue } from '../settings/settings-row.tsx';
import { SettingsSection } from '../settings/settings-section.tsx';
import { Textarea } from '../textarea/textarea.tsx';
import { TypeColorPicker } from '../type-pill/type-color-picker.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateDraft,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';
import {
  AUDIENCE_NONE,
  audienceChoice,
  audienceIds,
  templateAudienceOptions,
} from './template-audience-options.ts';
import { templateColorOf, templateColorVariant } from './template-color-variant.ts';
import { TemplateTopics, type KeyedTopic } from './template-topics.tsx';

const MEETING_TEMPLATE_KINDS: MeetingTemplateKind[] = ['one_on_one', 'team', 'other'];
const MEETING_TEMPLATE_SCOPES: MeetingTemplateScope[] = ['organisation', 'personal'];

export interface TemplateFormProps {
  /** The template being edited, or null while writing a new one. */
  template: MeetingTemplateDraft | null;
  /** Both empty leaves the audience field off the form. */
  tracks: MeetingTemplateTrackOption[];
  cohorts: MeetingTemplateCohortOption[];
  /** Says why there is no audience to pick instead of offering the field. */
  wholeOrganisationOnly: boolean;
  labels: MeetingTemplateEditorLabels;
  kind?: MeetingTemplateKind;
  kindEditable: boolean;
  /** Set, who the template is for is offered, starting from this answer. */
  scope?: MeetingTemplateScope;
  canDelete: boolean;
  /** Set, the delete stays off and this line says why. */
  deleteRefusal?: string;
  saving: boolean;
  error?: string;
  onSave: (draft: MeetingTemplateDraft) => void;
  onDelete?: () => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * The whole template is saved at once: the order of the topics is part of
 * what it says, so there is nothing to save halfway.
 */
export function TemplateForm({
  template,
  tracks,
  cohorts,
  wholeOrganisationOnly,
  labels,
  kind: initialKind,
  kindEditable,
  scope: initialScope,
  canDelete,
  deleteRefusal,
  saving,
  error,
  onSave,
  onDelete,
  onCancel,
}: TemplateFormProps) {
  const [name, setName] = useState(template?.name ?? '');
  const [color, setColor] = useState<MeetingTemplateColor>(template?.color ?? 'navy');
  const [kind, setKind] = useState(initialKind);
  const [scope, setScope] = useState(initialScope);
  const [description, setDescription] = useState(template?.description ?? '');
  const [audience, setAudience] = useState(
    template ? audienceChoice(template) : AUDIENCE_NONE,
  );
  /* A template only its writer sees has nobody to be aimed at. */
  const offersAudience =
    (tracks.length > 0 || cohorts.length > 0) && scope !== 'personal';
  const [topics, setTopics] = useState<KeyedTopic[]>(() =>
    (template?.sections ?? [{ title: '', description: '' }]).map((section, key) => ({
      ...section,
      key,
    })),
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function addTopic() {
    setTopics((current) => [
      ...current,
      {
        title: '',
        description: '',
        key: current.reduce((max, topic) => Math.max(max, topic.key), -1) + 1,
      },
    ]);
  }

  const complete =
    name.trim().length > 0 &&
    topics.length > 0 &&
    topics.every((topic) => topic.title.trim().length > 0);

  return (
    <>
      {onCancel && (
        <div className='mb-4'>
          <Button size='sm' variant='ghost' onClick={onCancel}>
            {labels.back}
          </Button>
        </div>
      )}

      <SettingsSection title={labels.title}>
        <SettingsRow label={labels.nameLabel}>
          <Input
            aria-label={labels.nameLabel}
            value={name}
            placeholder={labels.namePlaceholder}
            onChange={(event) => setName(event.target.value)}
          />
        </SettingsRow>
        <SettingsRow label={labels.descriptionLabel} description={labels.descriptionHint}>
          <Textarea
            aria-label={labels.descriptionLabel}
            value={description}
            placeholder={labels.descriptionPlaceholder}
            onChange={(event) => setDescription(event.target.value)}
          />
        </SettingsRow>
        <SettingsRow label={labels.colorLabel} description={labels.colorHint}>
          <TypeColorPicker
            aria-label={labels.colorLabel}
            options={MEETING_TEMPLATE_COLORS.map(templateColorVariant)}
            value={templateColorVariant(color)}
            labelOf={(variant) => labels.colors[templateColorOf(variant)]}
            onValueChange={(variant) => setColor(templateColorOf(variant))}
          />
        </SettingsRow>
        {kind && (
          <SettingsRow label={labels.kindLabel} description={labels.kindHint}>
            {kindEditable ? (
              <Select
                aria-label={labels.kindLabel}
                value={kind}
                options={MEETING_TEMPLATE_KINDS.map((value) => ({
                  value,
                  label: labels.kinds[value],
                }))}
                onChange={(event) => setKind(event.target.value as MeetingTemplateKind)}
              />
            ) : (
              <SettingsValue>{labels.kinds[kind]}</SettingsValue>
            )}
          </SettingsRow>
        )}
        {scope && (
          <SettingsRow label={labels.scopeLabel} description={labels.scopeHint}>
            <Select
              aria-label={labels.scopeLabel}
              value={scope}
              options={MEETING_TEMPLATE_SCOPES.map((value) => ({
                value,
                label: labels.scopes[value],
              }))}
              onChange={(event) => setScope(event.target.value as MeetingTemplateScope)}
            />
          </SettingsRow>
        )}
        {offersAudience && wholeOrganisationOnly && (
          <SettingsRow
            label={labels.audienceLabel}
            description={labels.audienceDefaultNote}
          >
            <SettingsValue>{labels.audienceNone}</SettingsValue>
          </SettingsRow>
        )}
        {offersAudience && !wholeOrganisationOnly && (
          <SettingsRow label={labels.audienceLabel} description={labels.audienceHint}>
            <Select
              aria-label={labels.audienceLabel}
              value={audience}
              options={templateAudienceOptions(tracks, cohorts, labels)}
              onChange={(event) => setAudience(event.target.value)}
            />
          </SettingsRow>
        )}
      </SettingsSection>

      <SettingsSection title={labels.topicsTitle} description={labels.topicsHint}>
        <TemplateTopics topics={topics} labels={labels} onChange={setTopics} />
        <div className='px-5.5 py-4'>
          <Button size='sm' variant='quiet' onClick={addTopic}>
            {labels.addTopic}
          </Button>
        </div>
      </SettingsSection>

      <div className='flex items-center gap-3'>
        <Button
          disabled={saving || !complete}
          onClick={() =>
            onSave({
              name: name.trim(),
              color,
              description: description.trim() || null,
              sections: topics.map((topic) => ({
                title: topic.title,
                description: topic.description,
              })),
              ...(kind && { kind }),
              ...(scope && { scope }),
              ...(offersAudience && !wholeOrganisationOnly && audienceIds(audience)),
            })
          }
        >
          {template ? labels.save : labels.create}
        </Button>
        {template && canDelete && (
          <Button
            variant='dangerQuiet'
            disabled={saving || Boolean(deleteRefusal)}
            onClick={() => setConfirmingDelete(true)}
          >
            {labels.delete}
          </Button>
        )}
        {template && canDelete && deleteRefusal && (
          <p className='text-sm text-ink-muted'>{deleteRefusal}</p>
        )}
        {error && (
          <p role='alert' className='text-sm text-danger'>
            {error}
          </p>
        )}
      </div>

      {template && canDelete && (
        <ConfirmDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={labels.deleteTitle}
          description={labels.deleteDescription({ name: template.name })}
          confirmLabel={labels.deleteConfirm}
          cancelLabel={labels.deleteCancel}
          variant='danger'
          onConfirm={async () => {
            /* A failure is the host's to report, through `error`. */
            try {
              await onDelete?.();
            } finally {
              setConfirmingDelete(false);
            }
          }}
        />
      )}
    </>
  );
}
